/**
 * Single source of truth for publicly quoted membership pricing.
 *
 * These figures mirror the live Stripe prices. The visible FAQ
 * (components/Faqs.tsx), the schema.org FAQPage mirror
 * (components/StructuredData.tsx) and the instrument-page summary
 * (components/MembershipPriceSummary.tsx) all read from here so they cannot
 * drift apart.
 *
 * Nobody is ever charged weekly or per lesson. Memberships are paid upfront
 * every 3 months or every 12 months. Do not publish a per-week figure: leads
 * who read "$57 per week" arrived at conversion expecting to pay weekly.
 */

export const REGISTRATION_FEE = '$99';

export type MembershipPricing = {
  /** The charge every 3 months, paid upfront. */
  quarterly: string;
  /** The charge every 12 months, paid upfront. */
  annually: string;
  /** Four quarterly payments minus one annual payment. */
  annualSaving: string;
  /** First debit on the 3-month plan: quarterly plus the registration fee. */
  firstQuarterlyPayment: string;
};

/** 30-minute weekly 1:1. Stripe: $801/quarter, $2,745/year. */
export const MUSO: MembershipPricing = {
  quarterly: '$801',
  annually: '$2,745',
  annualSaving: '$459',
  firstQuarterlyPayment: '$900',
};

/** 60-minute weekly 1:1. Stripe: $1,515/quarter, $5,515/year. */
export const HAPPY_MUSICKER: MembershipPricing = {
  quarterly: '$1,515',
  annually: '$5,515',
  annualSaving: '$545',
  firstQuarterlyPayment: '$1,614',
};

/** Graduated tiers: flat rate for the first 2 members, then per extra member. */
export const FAMILY = {
  firstTwoQuarterly: '$1,401',
  firstTwoAnnually: '$5,301',
  firstTwoAnnualSaving: '$303',
  additionalQuarterly: '$700.50',
  additionalAnnually: '$2,650.50',
} as const;
